import Phaser from 'phaser';

import SceneKeys from '../constants/SceneKeys';
import TextureKeys from '../constants/TextureKeys';
import RocketMouse from '../game/RocketMouse';
import LaserObstacle from '../game/LaserObstacle';

class Main extends Phaser.Scene {
  /** @type {Phaser.GameObjects.TileSprite} */
  background;

  /** @type {Phaser.GameObjects.Image} */
  mouseHole;

  /** @type {Phaser.GameObjects.Image} */
  window1;

  /** @type {Phaser.GameObjects.Image} */
  window2;

  /** @type {Phaser.GameObjects.Image[]} */
  windows = [];

  /** @type {Phaser.GameObjects.Image} */
  bookcase1;

  /** @type {Phaser.GameObjects.Image} */
  bookcase2;

  /** @type {Phaser.GameObjects.Image[]} */
  bookcases;

  /** @type {RocketMouse} */
  mouse;

  /** @type {LaserObstacle} */
  laserObstacle;

  /** @type {Phaser.Physics.Arcade.StaticGroup} */
  coins;

  /** @type {Phaser.GameObjects.Text} */
  scoreLabel;

  score = 0;

  constructor() {
    super(SceneKeys.Main);
  }

  init() {
    this.score = 0;
  }

  create() {
    const { width, height } = this.scale;

    this.background = this.add
      .tileSprite(0, 0, width, height, TextureKeys.Background)
      .setOrigin(0, 0)
      .setScrollFactor(0, 0); // stop the background from being scrolled by camera

    this.mouseHole = this.add.image(
      Phaser.Math.Between(900, 1500), // x
      501, // y
      TextureKeys.MouseHole,
    );

    this.window1 = this.add.image(
      Phaser.Math.Between(900, 1300), // x
      200, // y
      TextureKeys.Window1,
    );

    this.window2 = this.add.image(
      Phaser.Math.Between(1600, 2000), // x
      200, // y
      TextureKeys.Window2,
    );

    this.windows = [this.window1, this.window2];

    this.bookcase1 = this.add
      .image(
        Phaser.Math.Between(2200, 2700), // x
        580, // y
        TextureKeys.Bookcase1,
      )
      .setOrigin(0.5, 1);

    this.bookcase2 = this.add
      .image(
        Phaser.Math.Between(2900, 3400), // x
        580, // y
        TextureKeys.Bookcase2,
      )
      .setOrigin(0.5, 1);

    this.bookcases = [this.bookcase1, this.bookcase2];

    // create RocketMouse instance
    this.mouse = new RocketMouse(this, width * 0.5, height - 30);
    this.add.existing(this.mouse);

    // create LaserObstacle instance
    this.laserObstacle = new LaserObstacle(this, 900, 100);
    this.add.existing(this.laserObstacle);

    this.coins = this.physics.add.staticGroup();
    this.spawnCoins();

    this.scoreLabel = this.add
      .text(10, 10, `score: ${this.score}`, {
        fontSize: '24px',
        color: '#080808',
        backgroundColor: '#F8E71C',
        shadow: { fill: true, blur: 0, offsetY: 0 },
        padding: {
          left: 15,
          right: 15,
          top: 10,
          bottom: 10,
        },
      })
      .setScrollFactor(0);

    const { body } = this.mouse;
    body.setCollideWorldBounds(true);
    body.setVelocityX(200); // move right

    this.physics.world.setBounds(0, 0, Number.MAX_SAFE_INTEGER, height - 55);

    this.physics.add.overlap(
      this.laserObstacle,
      this.mouse,
      this.handleOverlapLaser,
      undefined,
      this,
    );

    this.physics.add.overlap(
      this.coins,
      this.mouse,
      this.handleCollectCoin,
      undefined,
      this,
    );

    this.cameras.main.startFollow(this.mouse);
    this.cameras.main.setBounds(0, 0, Number.MAX_SAFE_INTEGER, height);
  }

  update() {
    this.background.setTilePosition(this.cameras.main.scrollX);
    this.wrapMouseHole();
    this.wrapWindows();
    this.wrapBookcases();
    this.wrapLaserObstacle();
  }

  wrapMouseHole() {
    const { scrollX } = this.cameras.main;
    const rightEdge = scrollX + this.scale.width;

    // scroll off the left side of the screen
    if (this.mouseHole.x + this.mouseHole.width < scrollX) {
      this.mouseHole.x = Phaser.Math.Between(rightEdge + 100, rightEdge + 1000);
    }
  }

  wrapWindows() {
    const { scrollX } = this.cameras.main;
    const rightEdge = scrollX + this.scale.width;

    let width = this.window1.width * 2;
    if (this.window1.x + width < scrollX) {
      this.window1.x = Phaser.Math.Between(rightEdge + width, rightEdge + 800);

      const isOverlap = this.bookcases.find(
        (bookcase) =>
          Math.abs(this.window1.x - bookcase.x) <= this.window1.width,
      );

      this.window1.visible = !isOverlap;
    }

    width = this.window2.width * 2;
    if (this.window2.x + width < scrollX) {
      this.window2.x = Phaser.Math.Between(
        this.window1.x + width,
        this.window1.x + 800,
      );

      const isOverlap = this.bookcases.find(
        (bookcase) =>
          Math.abs(this.window2.x - bookcase.x) <= this.window2.width,
      );

      this.window2.visible = !isOverlap;
    }
  }

  wrapBookcases() {
    const { scrollX } = this.cameras.main;
    const rightEdge = scrollX + this.scale.width;

    let width = this.bookcase1.width * 2;
    if (this.bookcase1.x + width < scrollX) {
      this.bookcase1.x = Phaser.Math.Between(
        rightEdge + width,
        rightEdge + 800,
      );

      const isOverlap = this.windows.find(
        (window) =>
          Math.abs(this.bookcase1.x - window.x) <= this.bookcase1.width,
      );

      this.bookcase1.visible = !isOverlap;
    }

    width = this.bookcase2.width * 2;
    if (this.bookcase2.x + width < scrollX) {
      this.bookcase2.x = Phaser.Math.Between(
        this.bookcase1.x + width,
        this.bookcase1.x + 800,
      );

      const isOverlap = this.windows.find(
        (window) =>
          Math.abs(this.bookcase2.x - window.x) <= this.bookcase2.width,
      );

      this.bookcase2.visible = !isOverlap;

      this.spawnCoins();
    }
  }

  wrapLaserObstacle() {
    const { scrollX } = this.cameras.main;
    const rightEdge = scrollX + this.scale.width;

    const { width, body } = this.laserObstacle;
    if (this.laserObstacle.x + width < scrollX) {
      this.laserObstacle.x = Phaser.Math.Between(
        rightEdge + width,
        rightEdge + width + 1000,
      );
      this.laserObstacle.y = Phaser.Math.Between(0, 300);

      body.position.x = this.laserObstacle.x + body.offset.x;
      body.position.y = this.laserObstacle.y + 15;
    }
  }

  handleOverlapLaser() {
    this.mouse.kill();
  }

  handleCollectCoin(obj1, obj2) {
    const coin = obj2;
    this.coins.killAndHide(coin);
    coin.body.enable = false;

    this.score += 1;
    this.scoreLabel.text = `Score: ${this.score}`;
  }

  spawnCoins() {
    this.coins.children.each((child) => {
      const coin = child;
      this.coins.killAndHide(coin);
      coin.body.enable = false;
    });

    const { scrollX } = this.cameras.main;
    const rightEdge = scrollX + this.scale.width;

    let x = rightEdge + 100;
    const numCoins = Phaser.Math.Between(1, 20);

    for (let i = 0; i < numCoins; ++i) {
      const coin = this.coins.get(
        x,
        Phaser.Math.Between(100, this.scale.height - 100),
        TextureKeys.Coin,
      );

      coin.setVisible(true);
      coin.setActive(true);

      const { body } = coin;
      body.setCircle(body.width * 0.5);
      body.enable = true;

      // update the body x, y position from the GameObject
      body.updateFromGameObject();
      x += coin.width * 1.5;
    }
  }
}

export default Main;
